import React, { useContext, useMemo, useState } from "react";
import moment from "moment";
import { Select } from "@mantine/core";
import { DatePicker } from "@mantine/dates";
import { HiUserGroup, HiCheckCircle, HiClipboardCheck, HiChartBar, HiTrendingUp } from "react-icons/hi";
import { EButton, EPageTitle } from "../../libs/ui";
import PageLayout from "../../PageLayout";
import AttendanceStatCard from "../TeacherTrainingAttendance/components/AttendanceStatCard"; // reused as-is
// import { AnalyticsCard, BarList, DonutChart, LineTrend, PrePostChart } from "./components/AnalyticsWidgets";
import { AppContext } from "../../AppContext";
import { ROLES } from "../../Routes/routes";
import useTeacherTrainingAnalytics from "./hook/useTeacherTrainingAnalytics";
import { AnalyticsCard, BarList, DonutChart, LineTrend, PrePostChart } from "./components/AnalyticsWidgets";
import ViewAllModal from "./components/ViewAllModal";


export default function TeacherTrainingAnalyticsDashboard() {
  const { role } = useContext(AppContext);
  const h = useTeacherTrainingAnalytics();
  const s = h.summary;
  const [showDistricts, setShowDistricts] = useState(false);
  const [showBlocks, setShowBlocks] = useState(false);
  const n = (v: any) => (v ?? "--");
  const { top3, bottom3 } = useMemo(() => {
    const rows = [...(s?.districtPerformance ?? [])]
      .filter((r: any) => r?.postTestAverage != null)
      .sort((a: any, b: any) => b.postTestAverage - a.postTestAverage); // high → low

    return {
      top3: rows.slice(0, 3),
      // lowest 3, with the worst district first
      bottom3: rows.slice(-3).reverse(),
    };
  }, [s?.districtPerformance]);
 

  const bands = [
    {
      label: "Excellent (80-100%)",
      value: s?.statePerformance?.excellentPercentage ?? 0,
      color: "#16a34a",
    },
    {
      label: "Good (60-79%)",
      value: s?.statePerformance?.goodPercentage ?? 0,
      color: "#3b82f6",
    },
    {
      label: "Average (40-59%)",
      value: s?.statePerformance?.averagePercentage ?? 0,
      color: "#f59e0b",
    },
    {
      label: "Needs Support (<40%)",
      value: s?.statePerformance?.needsSupportPercentage ?? 0,
      color: "#ef4444",
    },
  ];
  const blockedByRole = role?.includes(ROLES.BLOCK) || role?.includes(ROLES.DISTRICT);
  const districtName = h.districts?.find((d: any) => String(d.id) === h.appliedDistrict)?.name;

  const viewAllBtn = (label: string, onClick: () => void) => (
    <div className="flex justify-end mt-3">
      <button
        type="button"
        onClick={onClick}
        className="text-xs font-bold text-blue-600 hover:underline bg-transparent border-0 cursor-pointer"
      >
        {label} →
      </button>
    </div>
  );

  return (
    <PageLayout>
      {/* Header */}
      <div className="flex justify-between items-center">
        <EPageTitle
          title="Teacher Training Analytics"
          description="Pre-test and post-test performance across the training cascade"
        />
        <div className="flex items-center gap-4 text-sm text-gray-500">
          {/* <span>
            Last Updated:{" "}
            {moment(h.lastUpdated).format("hh:mm A | DD MMM YYYY")}
          </span>
          <EButton variant="subtle" onClick={h.handleApplyFilter}>
            Refresh
          </EButton> */}
          <EButton
            className="btn-blue"
            loading={h.isDownloadingReport}
            onClick={h.downloadReport}
          >
            Download Report
          </EButton>
        </div>
      </div>

      {/* Filters */}
      <div className="filter-container mt-4">
        <div className="flex justify-between gap-3 items-end">
          <Select
            label="Select Session"
            labelProps={{ className: "font-semibold" }}
            placeholder="Select Session"
            data={(h.sessions ?? [])
              .filter((e: any) => e?.session_Id === 4)
              .map((e: any) => ({
                value: String(e.session_Id),
                label: e.session_Name,
              }))}
            value={h.selectedSession}
            onChange={(e) => h.setSelectedSession(String(e))}
          />
          <Select
            label="Training Batch"
            labelProps={{ className: "font-semibold" }}
            placeholder="Select Training Batch"
            clearable
            data={h.trainingTitleOptions?.map((e: any) => ({
              value: String(e?.training_Schedule_Header_Id),
              label: e?.schedule_Header_Title,
            }))}
            value={h.trainingTitle}
            onChange={(e) => {
              h.setTrainingTitle(String(e));
              h.setSelectedDate(null);
            }}
          />
          <DatePicker
            label="Select Date"
            labelProps={{ className: "font-semibold" }}
            placeholder="Select date"
            minDate={h.dateRange?.min}
            maxDate={h.dateRange?.max}
            clearable={false}
            value={h.selectedDate}
            onChange={(e: any) => h.setSelectedDate(e)}
          />
          <Select
            label="District"
            labelProps={{ className: "font-semibold" }}
            placeholder="All Districts"
            clearable
            disabled={!!blockedByRole}
            data={
              h.districts?.map((d: any) => ({
                label: d.name,
                value: String(d.id),
              })) ?? []
            }
            value={h.selectedDistrict ?? null}
            onChange={(e: any) => {
              h.setSelectedBlock(undefined);
              h.setSelectedDistrict(e);
            }}
          />
          <Select
            label="Block"
            labelProps={{ className: "font-semibold" }}
            placeholder="All Blocks"
            clearable
            disabled={!h.selectedDistrict || role?.includes(ROLES.BLOCK)}
            data={
              h.blocks?.map((b: any) => ({
                label: b.name,
                value: String(b.id),
              })) ?? []
            }
            value={h.selectedBlock ?? null}
            onChange={(e: any) => h.setSelectedBlock(e)}
          />
          <div className="flex gap-3">
            <EButton className="btn-blue" onClick={h.handleApplyFilter}>
              Search
            </EButton>
            <EButton variant="outline" onClick={h.handleClearFilter}>
              Clear
            </EButton>
          </div>
        </div>
      </div>

      {/* KPI strip */}
      <div className="flex gap-4 mt-6 flex-wrap">
        <AttendanceStatCard
          label="Teachers Assigned"
          value={n(s?.totalTeachers)}
          icon={HiUserGroup}
          isLoading={h.isLoading}
          textClass="text-green-600"
        />
        <AttendanceStatCard
          label="Completed Pre-Test"
          value={n(s?.preTestCompletedCount)}
          icon={HiCheckCircle}
          isLoading={h.isLoading}
          textClass="text-green-600"
        />
        <AttendanceStatCard
          label="Completed Post-Test"
          value={n(s?.postTestCompletedCount)}
          icon={HiClipboardCheck}
          isLoading={h.isLoading}
          textClass="text-green-600"
        />
        {/* <AttendanceStatCard label="Average Pre Score" value={s ? `${s.avgPreScore}%` : "--"} bgClass="bg-amber-50" textClass="text-amber-600" icon={HiChartBar} isLoading={h.isLoading} />
        <AttendanceStatCard label="Average Post Score" value={s ? `${s.avgPostScore}%` : "--"} bgClass="bg-green-50" textClass="text-green-600" icon={HiChartBar} isLoading={h.isLoading} />
        <AttendanceStatCard label="Learning Gain" value={s ? `+${s.learningGain}%` : "--"} bgClass="bg-blue-50" textClass="text-blue-600" icon={HiTrendingUp} isLoading={h.isLoading} /> */}
      </div>

      {/* Row 1 */}
      <div className="grid grid-cols-2 gap-4 mt-6">
        <AnalyticsCard
          title="State Performance"
          subtitle="Overall"
          isLoading={h.isLoading}
        >
          <div className="flex items-center gap-6">
            <DonutChart segments={bands} />
            <ul className="text-xs flex flex-col gap-2 flex-1">
              {bands.map((b) => (
                <li key={b.label} className="flex items-center gap-2">
                  <span
                    className="w-2 h-2 rounded-full"
                    style={{ background: b.color }}
                  />
                  <span className="flex-1">{b.label}</span>
                  <span className="font-semibold">{b.value}%</span>
                </li>
              ))}
            </ul>
            <div className="bg-gray-50 rounded-lg p-3 text-xs min-w-[130px]">
              <div className="text-gray-500">
                Pre Test{" "}
                <b className="text-amber-500">
                  {n(s?.statePerformance?.preTestAverage)}%
                </b>
              </div>
              <div className="text-gray-500 mt-1">
                Post Test{" "}
                <b className="text-green-600">
                  {n(s?.statePerformance?.postTestAverage)}%
                </b>
              </div>
              <div className="text-gray-500 mt-1">
                Learning Gain{" "}
                <b className="text-blue-600">
                  {n(s?.statePerformance?.learningGain)}%
                </b>
              </div>
            </div>
          </div>
        </AnalyticsCard>
        <AnalyticsCard
          title="Pre vs Post Test"
          subtitle="State Level"
          isLoading={h.isLoading}
        >
          <PrePostChart
            pre={s?.preVsPostTest?.preTestAverage ?? 0}
            post={s?.preVsPostTest?.postTestAverage ?? 0}
            lean={s?.preVsPostTest?.learningGain ?? 0}
          />
        </AnalyticsCard>
      </div>

      {/* Row 2 */}
      <div className="grid grid-cols-2 gap-4 mt-4">
        <AnalyticsCard
          title="District Performance"
          subtitle="By Post Test Score"
          isLoading={h.isLoading}
        >
          <BarList
            rows={s?.districtPerformance?.slice(0, 8) ?? []}
            name={"district_Name"}
            value={"postTestAverage"}
          />
          {viewAllBtn("View All Districts", () => setShowDistricts(true))}
        </AnalyticsCard>
        <AnalyticsCard
          title="Block Performance"
          subtitle={districtName ? `${districtName} District` : "All"}
          isLoading={h.isLoading}
        >
          <BarList
            rows={s?.blockPerformance?.slice(0, 8) ?? []}
            name={"block_Name"}
            value={"postTestAverage"}
            color="#3b82f6"
            emptyText="Select a district and click Search to see blocks"
          />
          {viewAllBtn("View All Blocks", () => setShowBlocks(true))}
        </AnalyticsCard>
      </div>

      {/* Row 3 */}
      <div className="grid grid-cols-2 gap-4 mt-4 mb-4">
        <AnalyticsCard
          title="Top & Bottom Performers"
          subtitle="District Level by Post Test Score"
          isLoading={h.isLoading}
        >
          <div className="grid grid-cols-2 gap-6">
            <div>
              <div className="text-xs font-semibold text-green-600 mb-2">
                Top 3 Districts
              </div>
              <BarList
                rows={top3}
                name={"district_Name"}
                value={"postTestAverage"}
              />
            </div>
            <div>
              <div className="text-xs font-semibold text-red-600 mb-2">
                Bottom 3 Districts
              </div>
              <BarList
                rows={bottom3}
                name={"district_Name"}
                value={"postTestAverage"}
                color="#ef4444"
              />
            </div>
          </div>
        </AnalyticsCard>

        <ViewAllModal
          opened={showDistricts}
          onClose={() => setShowDistricts(false)}
          title="District Performance"
          rows={s?.districtPerformance ?? []}
          nameKey="district_Name"
          nameLabel="District"
        />
        <ViewAllModal
          opened={showBlocks}
          onClose={() => setShowBlocks(false)}
          title={
            districtName
              ? `Block Performance – ${districtName}`
              : "Block Performance"
          }
          rows={s?.blockPerformance ?? []}
          nameKey="block_Name"
          nameLabel="Block"
        />
      </div>
    </PageLayout>
  );
}
